import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const FAQS = [
  { q: 'How can I crack CUET in the first attempt?', a: 'By understanding the exam pattern early, building a realistic study plan, practising consistently with NCERT and PYQs, and taking regular mock tests to build exam-day familiarity.' },
  { q: 'What is the best way to prepare for CUET?', a: 'A structured approach combining syllabus analysis, concept building, topic-wise practice, and regular mock testing tends to work best, sustained over several months.' },
  { q: 'How many hours should I study daily for CUET?', a: 'Consistency matters more than raw hours — a sustainable 2–3 hour daily routine over months generally outperforms sporadic long study sessions.' },
  { q: 'Is NCERT enough to crack CUET?', a: 'NCERT provides a strong foundation for most domain subjects, though pairing it with previous year papers and mock tests helps translate concept knowledge into exam-ready performance.' },
  { q: 'Are mock tests important for CUET?', a: 'Yes, mock tests are one of the strongest predictors of exam-day performance, building timing, stamina, and familiarity with the actual format.' },
  { q: 'Can I crack CUET without coaching?', a: "Yes, it's possible with strong self-discipline, though coaching can significantly reduce preparation gaps through structure, expert feedback, and regular testing." },
  { q: 'How long does it take to prepare for CUET?', a: 'Most students benefit from at least 4–6 months of consistent preparation, depending on their starting point and number of domain subjects.' },
  { q: 'Which books are best for CUET preparation?', a: 'NCERT textbooks remain the core resource for domain subjects, supplemented with previous year question papers and structured practice material.' },
  { q: 'How should I prepare for the General Test?', a: 'Practise quantitative aptitude and logical reasoning regularly, and build a habit of reviewing general knowledge and current affairs on a rolling basis throughout preparation.' },
  { q: 'Is online coaching useful for CUET preparation?', a: 'Yes, particularly for structure, expert guidance, and consistent mock testing — areas that are difficult to maintain reliably through self-study alone.' },
  { q: 'How does BESST help students crack CUET?', a: 'BESST provides live classes, expert faculty, personalised mentorship, structured study materials, and regular mock tests, built around the preparation approach outlined in this guide.' },
  { q: 'Does BESST provide mock tests and study materials?', a: "Yes, both are included as part of BESST's CUET coaching programme." },
  { q: 'Can I prepare for CUET along with my board exams?', a: 'Yes, with a realistic schedule that treats both as complementary priorities rather than competing ones.' },
  { q: 'What mistakes should I avoid while preparing for CUET?', a: 'Avoid ignoring NCERT, skipping mock tests, studying without a plan, neglecting revision, and leaving new topics for the last minute.' },
  { q: 'When should I start preparing for CUET?', a: 'Ideally, at least 4–6 months before the exam — earlier starts allow for a more balanced pace and reduce reliance on last-minute cramming.' },
];

const TEN_TIPS = [
  { n: '01', icon: '📋', title: 'Understand the Complete Syllabus', desc: "Know exactly what's being tested before deciding how to study — this prevents both over-preparation and dangerous gaps." },
  { n: '02', icon: '📚', title: 'Build Strong NCERT Fundamentals', desc: 'NCERT remains the base for most CUET domain subjects. Concepts built here carry directly into exam performance.' },
  { n: '03', icon: '📅', title: 'Create a Realistic Study Plan', desc: 'A plan that accounts for board exams and rest, not just study hours, is one a student can actually follow through to exam day.' },
  { n: '04', icon: '🎯', title: 'Focus on High-Weightage Topics', desc: "Not every topic contributes equally to the final score — prioritise the ones that appear most frequently across past papers." },
  { n: '05', icon: '📄', title: 'Practise Previous Year Question Papers', desc: "PYQs reveal NTA's actual question style and frequently tested areas in a way syllabus documents alone can't." },
  { n: '06', icon: '📝', title: 'Take Regular Mock Tests', desc: 'Mock tests build timing, stamina, and exam-day familiarity — benefits that compound the earlier they start.' },
  { n: '07', icon: '🔎', title: 'Analyse Your Mistakes', desc: 'Every mock test should end with a review of exactly what went wrong, not just a glance at the final score.' },
  { n: '08', icon: '🔄', title: 'Revise Consistently', desc: 'Spaced, repeated revision keeps earlier topics from fading by the time the exam arrives.' },
  { n: '09', icon: '⏱️', title: 'Improve Time Management', desc: 'Practising under strict timing — not open-ended conditions — is what actually builds exam-day pacing.' },
  { n: '10', icon: '🧘', title: 'Stay Calm and Maintain Consistency', desc: 'A steady, sustainable routine over months outperforms an intense but short-lived push right before the exam.' },
];

const BESST_FEATURES = [
  { icon: '📡', title: 'Live Interactive Classes', desc: 'Real-time teaching across every CUET section, with the ability to resolve doubts as they come up.' },
  { icon: '🎓', title: 'Expert Faculty', desc: "Faculty with specific CUET teaching experience, focused on the exam's actual format rather than a generalised curriculum." },
  { icon: '🤝', title: 'Personalised Mentorship', desc: 'One-to-one guidance to work through individual weak areas, identified through consistent testing.' },
  { icon: '📚', title: 'Comprehensive Study Materials', desc: 'Structured, syllabus-aligned notes that remove the guesswork of sourcing reliable material independently.' },
  { icon: '✏️', title: 'Topic-wise Practice', desc: 'Focused practice sets after each topic, catching gaps early rather than at the mock test stage.' },
  { icon: '📄', title: 'Previous Year Question Papers', desc: "Dedicated sessions built around recognising NTA's recurring question patterns." },
  { icon: '📝', title: 'Full-Length Mock Tests', desc: 'A consistent, scheduled testing cycle that builds genuine exam-day familiarity well ahead of time.' },
  { icon: '📊', title: 'Performance Tracking', desc: 'Detailed analytics after every test, turning results into a specific, actionable next step.' },
  { icon: '💬', title: 'Doubt Solving Sessions', desc: 'Dedicated time to resolve doubts before they compound into larger gaps.' },
  { icon: '🏫', title: 'Career & Admission Guidance', desc: 'Support that extends beyond the exam itself, into university and course selection.' },
];

const TOC = [
  { id: 'first-attempt', label: 'Can You Crack in First Attempt?' },
  { id: 'understand-exam', label: 'Understand the Exam First' },
  { id: 'ten-tips', label: '10 Proven Tips' },
  { id: 'subject-wise', label: 'Subject-wise Tips' },
  { id: 'roadmap', label: '60-Day Roadmap' },
  { id: 'mistakes', label: 'Common Mistakes' },
  { id: 'resources', label: 'Best Resources' },
  { id: 'besst-helps', label: 'How BESST Helps' },
  { id: 'comparison', label: 'Self-Study vs Coaching' },
  { id: 'exam-day', label: 'Exam-Day Tips' },
  { id: 'faqs', label: 'FAQs' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Crack CUET: 10 Proven Tips',
  description: 'A step-by-step guide on how to crack CUET in the first attempt with expert preparation tips and strategies.',
  step: TEN_TIPS.map(s => ({
    '@type': 'HowToStep',
    name: s.title,
    text: s.desc,
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://besst.in' },
    { '@type': 'ListItem', position: 2, name: 'CUET Coaching', item: 'https://besst.in/cuet-coaching' },
    { '@type': 'ListItem', position: 3, name: 'How to Crack CUET', item: 'https://besst.in/how-to-crack-cuet' },
  ],
};

const CSS = `
  .hc-page{background:linear-gradient(160deg,#0c0420 0%,#140831 50%,#0c0420 100%);min-height:100vh;color:#e8deff;font-family:'Inter','Segoe UI',system-ui,sans-serif;}
  .hc-breadcrumb{max-width:1200px;margin:0 auto;padding:1rem 1.5rem 0;font-size:.8rem;color:#9ca3af;display:flex;align-items:center;gap:.4rem;flex-wrap:wrap;}
  .hc-breadcrumb a{color:#a78bfa;text-decoration:none;}.hc-breadcrumb a:hover{color:#d4af37;}
  .hc-hero{padding:3rem 1.5rem 3.5rem;max-width:860px;margin:0 auto;text-align:center;}
  .hc-badge{display:inline-block;background:rgba(212,175,55,.12);color:#d4af37;border:1px solid rgba(212,175,55,.3);border-radius:20px;padding:.35rem 1rem;font-size:.78rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:1.25rem;}
  .hc-hero h1{font-size:clamp(1.75rem,4vw,2.7rem);font-weight:800;line-height:1.2;margin:0 0 1.25rem;color:#f0e6ff;}
  .hc-hero h1 em{font-style:normal;background:linear-gradient(90deg,#d4af37,#f59e0b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .hc-intro{font-size:1.05rem;line-height:1.8;color:#c4b5fd;max-width:720px;margin:0 auto 2rem;}
  .hc-hero-cta{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}
  .btn-gold{background:linear-gradient(135deg,#d4af37,#f59e0b);color:#0c0420;font-weight:700;padding:.85rem 2rem;border-radius:10px;border:none;cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:transform .2s,box-shadow .2s;}
  .btn-gold:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(212,175,55,.35);}
  .btn-outline{background:transparent;color:#e8deff;font-weight:600;padding:.85rem 2rem;border-radius:10px;border:1.5px solid rgba(232,222,255,.25);cursor:pointer;font-size:.95rem;text-decoration:none;display:inline-block;transition:border-color .2s,background .2s;}
  .btn-outline:hover{border-color:#a78bfa;background:rgba(167,139,250,.08);}
  .hc-qa-box{background:rgba(212,175,55,.07);border:1px solid rgba(212,175,55,.2);border-radius:12px;padding:1.25rem 1.5rem;margin-bottom:2rem;}
  .hc-qa-label{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#d4af37;margin-bottom:.5rem;}
  .hc-qa-text{font-size:.93rem;line-height:1.75;color:#c4b5fd;}
  .hc-layout{max-width:1200px;margin:0 auto;padding:3rem 1.5rem;display:grid;grid-template-columns:220px 1fr;gap:3rem;align-items:start;}
  @media(max-width:900px){.hc-layout{grid-template-columns:1fr;}}
  .hc-toc{position:sticky;top:80px;}
  @media(max-width:900px){.hc-toc{display:none;}}
  .hc-toc-title{font-size:.72rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#7c6b9e;margin-bottom:.75rem;}
  .hc-toc ul{list-style:none;padding:0;margin:0;}.hc-toc li{margin-bottom:.15rem;}
  .hc-toc button{background:none;border:none;cursor:pointer;text-align:left;width:100%;padding:.4rem .6rem;font-size:.82rem;color:#9ca3af;border-radius:6px;border-left:2px solid transparent;transition:all .15s;line-height:1.4;}
  .hc-toc button:hover{color:#e8deff;background:rgba(255,255,255,.05);}
  .hc-toc button.active{color:#d4af37;border-left-color:#d4af37;background:rgba(212,175,55,.07);font-weight:600;}
  .hc-section{margin-bottom:3.5rem;scroll-margin-top:90px;}
  .hc-h2{font-size:clamp(1.25rem,2.5vw,1.65rem);font-weight:700;color:#f0e6ff;margin:0 0 1.25rem;padding-bottom:.6rem;border-bottom:1px solid rgba(212,175,55,.2);}
  .hc-h3{font-size:1.02rem;font-weight:700;color:#d4af37;margin:1.25rem 0 .45rem;}
  .hc-p{font-size:.97rem;line-height:1.8;color:#c4b5fd;margin:0 0 1rem;}
  .hc-parent-link{display:inline-flex;align-items:center;gap:.4rem;font-size:.82rem;color:#7c6b9e;text-decoration:none;margin-bottom:.75rem;}
  .hc-parent-link:hover{color:#a78bfa;}
  .hc-bullets{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:.65rem;}
  .hc-bullets li{display:flex;gap:.75rem;align-items:flex-start;font-size:.95rem;line-height:1.65;color:#c4b5fd;}
  .hc-bi{width:22px;height:22px;background:rgba(212,175,55,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.7rem;color:#d4af37;flex-shrink:0;margin-top:2px;}
  .hc-bs{color:#e8deff;font-weight:600;}
  .hc-exam-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:600px){.hc-exam-grid{grid-template-columns:1fr;}}
  .hc-exam-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;display:flex;gap:.75rem;align-items:flex-start;transition:border-color .2s;}
  .hc-exam-card:hover{border-color:rgba(212,175,55,.2);}
  .hc-exam-icon{font-size:1.3rem;width:38px;height:38px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .hc-exam-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .hc-exam-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .hc-tips-numbered{display:flex;flex-direction:column;gap:.75rem;}
  .hc-tip-card{display:flex;gap:1rem;align-items:flex-start;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.15rem;transition:border-color .2s,background .2s;}
  .hc-tip-card:hover{border-color:rgba(212,175,55,.25);background:rgba(255,255,255,.05);}
  .hc-tip-left{display:flex;flex-direction:column;align-items:center;flex-shrink:0;gap:2px;}
  .hc-tip-num{font-size:.68rem;font-weight:800;color:#d4af37;line-height:1;}
  .hc-tip-icon-badge{width:40px;height:40px;background:linear-gradient(135deg,rgba(212,175,55,.2),rgba(212,175,55,.08));border:1.5px solid rgba(212,175,55,.3);border-radius:8px;display:flex;flex-direction:column;align-items:center;justify-content:center;flex-shrink:0;}
  .hc-tip-emoji{font-size:.95rem;}
  .hc-tip-card h3{font-size:.92rem;font-weight:700;color:#f0e6ff;margin:0 0 .25rem;}
  .hc-tip-card p{font-size:.85rem;line-height:1.6;color:#9ca3af;margin:0;}
  .hc-subject-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}
  @media(max-width:640px){.hc-subject-grid{grid-template-columns:1fr;}}
  .hc-sub-card{background:rgba(124,58,237,.08);border:1px solid rgba(124,58,237,.2);border-radius:12px;padding:1.1rem 1.25rem;}
  .hc-sub-card h3{font-size:.93rem;font-weight:700;color:#a78bfa;margin:0 0 .5rem;}
  .hc-sub-card p,.hc-sub-card li{font-size:.85rem;line-height:1.65;color:#9ca3af;margin:0;}
  .hc-sub-card ul{list-style:none;padding:0;display:flex;flex-direction:column;gap:.35rem;}
  .hc-sub-card ul li::before{content:'▸ ';color:#7c3aed;font-size:.75rem;}
  .hc-roadmap-table{width:100%;border-collapse:collapse;font-size:.9rem;}
  .hc-roadmap-table thead tr{background:rgba(212,175,55,.12);}
  .hc-roadmap-table th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;}
  .hc-roadmap-table td{padding:.85rem 1.25rem;border:1px solid rgba(255,255,255,.06);color:#c4b5fd;vertical-align:top;}
  .hc-roadmap-table tbody tr:nth-child(odd){background:rgba(124,58,237,.06);}
  .hc-roadmap-table tbody tr:nth-child(even){background:rgba(255,255,255,.025);}
  .hc-roadmap-table tbody tr:hover{background:rgba(212,175,55,.05);}
  .hc-phase-badge{display:inline-block;background:rgba(212,175,55,.15);color:#d4af37;border-radius:6px;padding:.2rem .65rem;font-size:.78rem;font-weight:700;}
  .hc-mistakes-grid{display:flex;flex-direction:column;gap:.6rem;}
  .hc-mistake{display:flex;gap:.75rem;align-items:flex-start;background:rgba(239,68,68,.05);border:1px solid rgba(239,68,68,.15);border-radius:8px;padding:.85rem 1rem;}
  .hc-mistake-x{color:#ef4444;font-size:1rem;flex-shrink:0;margin-top:1px;font-weight:700;}
  .hc-mistake p{font-size:.88rem;line-height:1.55;color:#c4b5fd;margin:0;}
  .hc-mistake strong{color:#f0e6ff;}
  .hc-resources-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:600px){.hc-resources-grid{grid-template-columns:1fr;}}
  .hc-res-card{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s;}
  .hc-res-card:hover{border-color:rgba(212,175,55,.25);}
  .hc-res-icon{font-size:1.4rem;margin-bottom:.5rem;}
  .hc-res-card h3{font-size:.9rem;font-weight:700;color:#f0e6ff;margin:0 0 .35rem;}
  .hc-res-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .hc-besst-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.85rem;}
  @media(max-width:640px){.hc-besst-grid{grid-template-columns:1fr;}}
  .hc-besst-card{display:flex;gap:.85rem;align-items:flex-start;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;padding:1rem 1.1rem;transition:border-color .2s,background .2s;}
  .hc-besst-card:hover{border-color:rgba(212,175,55,.25);background:rgba(255,255,255,.05);}
  .hc-besst-icon{font-size:1.4rem;width:42px;height:42px;display:flex;align-items:center;justify-content:center;background:rgba(212,175,55,.1);border-radius:8px;flex-shrink:0;}
  .hc-besst-card h3{font-size:.88rem;font-weight:700;color:#f0e6ff;margin:0 0 .3rem;}
  .hc-besst-card p{font-size:.83rem;line-height:1.55;color:#9ca3af;margin:0;}
  .hc-cmp-wrap{overflow-x:auto;border-radius:12px;}
  .hc-cmp-table{width:100%;border-collapse:collapse;font-size:.9rem;}
  .hc-cmp-table thead tr{background:rgba(212,175,55,.12);}
  .hc-cmp-table th{padding:.85rem 1.25rem;text-align:left;font-weight:700;color:#d4af37;border:1px solid rgba(212,175,55,.15);font-size:.85rem;}
  .hc-cmp-table td{padding:.75rem 1.25rem;border:1px solid rgba(255,255,255,.06);color:#c4b5fd;}
  .hc-cmp-table tbody tr:nth-child(even){background:rgba(255,255,255,.025);}
  .hc-cmp-table tbody tr:hover{background:rgba(212,175,55,.05);}
  .hc-better{color:#22c55e;font-weight:600;}
  .hc-exam-tips{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:600px){.hc-exam-tips{grid-template-columns:1fr;}}
  .hc-exam-tip{display:flex;gap:.65rem;align-items:flex-start;background:rgba(34,197,94,.05);border:1px solid rgba(34,197,94,.15);border-radius:8px;padding:.85rem 1rem;font-size:.88rem;line-height:1.55;color:#c4b5fd;}
  .hc-exam-tip-check{color:#22c55e;flex-shrink:0;font-size:1rem;margin-top:1px;}
  .hc-faq-list{display:flex;flex-direction:column;gap:.6rem;}
  .hc-faq-item{background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:10px;overflow:hidden;transition:border-color .2s;}
  .hc-faq-item.open{border-color:rgba(212,175,55,.3);}
  .hc-faq-q{width:100%;background:none;border:none;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:1rem 1.25rem;text-align:left;font-size:.93rem;font-weight:600;color:#e8deff;transition:color .15s;}
  .hc-faq-q:hover{color:#d4af37;}
  .hc-faq-chevron{font-size:.75rem;color:#7c6b9e;transition:transform .25s;flex-shrink:0;}
  .hc-faq-item.open .hc-faq-chevron{transform:rotate(180deg);color:#d4af37;}
  .hc-faq-a{max-height:0;overflow:hidden;transition:max-height .3s ease;}
  .hc-faq-item.open .hc-faq-a{max-height:500px;}
  .hc-faq-a-inner{padding:.85rem 1.25rem 1rem;font-size:.88rem;line-height:1.75;color:#9ca3af;border-top:1px solid rgba(255,255,255,.06);}
  .hc-links-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.65rem;}
  @media(max-width:540px){.hc-links-grid{grid-template-columns:1fr;}}
  .hc-ilink{display:flex;align-items:center;gap:.6rem;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:8px;padding:.7rem 1rem;text-decoration:none;color:#a78bfa;font-size:.88rem;font-weight:500;transition:border-color .2s,background .2s;}
  .hc-ilink:hover{border-color:rgba(167,139,250,.4);background:rgba(167,139,250,.06);color:#c4b5fd;}
  .hc-cta{background:linear-gradient(135deg,rgba(124,58,237,.25) 0%,rgba(212,175,55,.12) 100%);border:1px solid rgba(212,175,55,.2);border-radius:16px;padding:2.5rem;text-align:center;margin-top:1rem;}
  .hc-cta h2{font-size:1.5rem;font-weight:800;color:#f0e6ff;margin:0 0 .75rem;}
  .hc-cta p{font-size:.95rem;line-height:1.7;color:#c4b5fd;max-width:600px;margin:0 auto 1.5rem;}
`;

const HowToCrackCuetPage = () => {
  const [activeSection, setActiveSection] = useState('first-attempt');
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    TOC.forEach(t => { const el = document.getElementById(t.id); if (el) observerRef.current.observe(el); });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Helmet>
        <title>How to Crack CUET: Expert Preparation Tips & Strategy</title>
        <meta name="description" content="Learn how to crack CUET with expert preparation tips, study plans, mock test strategies, and subject-wise guidance. Prepare smarter with BESST." />
        <meta name="keywords" content="how to crack cuet, cuet preparation tips, how to crack cuet in first attempt, cuet preparation strategy, how to prepare for cuet, cuet study plan, cuet exam preparation, how to score good marks in cuet, cuet preparation at home, cuet mock test" />
        <meta property="og:title" content="How to Crack CUET: Expert Preparation Tips & Strategy" />
        <meta property="og:description" content="Learn how to crack CUET with expert preparation tips, study plans, mock test strategies, and subject-wise guidance. Prepare smarter with BESST." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://besst.in/how-to-crack-cuet" />
        <link rel="canonical" href="https://besst.in/how-to-crack-cuet" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="hc-page mt-20">
        <style>{CSS}</style>

        <nav className="hc-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span>›</span>
          <Link to="/cuet-coaching">CUET Coaching</Link><span>›</span>
          <span style={{ color: '#e8deff' }}>How to Crack CUET</span>
        </nav>

        <div className="hc-hero">
          <div className="hc-badge">Expert Tips & Strategy</div>
          <h1>
            <em>How to Crack CUET:</em> Expert Preparation Tips to Score High in Your First Attempt
          </h1>
          <p className="hc-intro">
            The students who consistently score well on CUET aren't necessarily the ones who put in the most hours — they're the ones who understood the exam early, built a realistic plan around it, and stuck to consistent practice and revision instead of last-minute cramming. This guide answers the common first-time aspirant questions directly, with a practical, execution-focused approach.
          </p>
          <div className="hc-hero-cta">
            <Link to="/subscriptions" className="btn-gold">Get Expert Guidance →</Link>
            <Link to="/cuet-mock-test" className="btn-outline">Try a Free Mock Test</Link>
          </div>
        </div>

        <div className="hc-layout">
          <aside className="hc-toc" aria-label="Table of contents">
            <div className="hc-toc-title">On This Page</div>
            <ul>
              {TOC.map(t => (
                <li key={t.id}>
                  <button className={activeSection === t.id ? 'active' : ''} onClick={() => scrollTo(t.id)}>{t.label}</button>
                </li>
              ))}
            </ul>
          </aside>

          <main>
            <Link to="/cuet-coaching" className="hc-parent-link">← Back to CUET Coaching</Link>

            {/* Quick answer */}
            <div className="hc-qa-box">
              <div className="hc-qa-label">Quick Answer</div>
              <p className="hc-qa-text">Cracking CUET requires understanding the syllabus, following a structured study plan, practising mock tests, revising consistently, and analysing mistakes. With expert guidance and regular assessments, students can significantly improve their chances of securing a high score — even in their first attempt.</p>
            </div>

            {/* First attempt */}
            <section id="first-attempt" className="hc-section">
              <h2 className="hc-h2">Can You Crack CUET in the First Attempt?</h2>
              <p className="hc-p">Yes — with the right strategy, a first attempt at CUET is very much crackable. What separates a strong first attempt from a disappointing one usually comes down to a few things:</p>
              <ul className="hc-bullets">
                {[
                  ['Consistency over last-minute preparation', 'Students who spread preparation across months, rather than compressing it into the final weeks, tend to retain more and perform more steadily under exam pressure.'],
                  ["Understanding the exam before starting", "Jumping straight into random practice without first understanding CUET's structure often wastes early preparation time."],
                  ['Setting realistic goals', 'A first-time aspirant aiming to master every topic equally well within a tight timeline usually ends up mastering none of them — prioritisation matters more than coverage.'],
                ].map(([title, desc]) => (
                  <li key={title}><span className="hc-bi">✓</span><span><span className="hc-bs">{title}: </span>{desc}</span></li>
                ))}
              </ul>
              <p className="hc-p" style={{ marginTop: '1rem' }}>First attempts don't fail because students aren't capable. They usually fail because preparation started reactively instead of with a plan.</p>
            </section>

            {/* Understand exam */}
            <section id="understand-exam" className="hc-section">
              <h2 className="hc-h2">Understand the CUET Exam Before You Start Preparing</h2>
              <p className="hc-p">Understanding the exam first isn't a delay — it's what makes every hour of preparation afterward more targeted and efficient.</p>
              <div className="hc-exam-grid">
                {[
                  ['📋', 'Know the Exam Pattern', "Understanding how CUET is structured — sections, sequencing, and timing — before preparation begins means nothing about exam day comes as a surprise."],
                  ['➕', 'Understand the Marking Scheme', 'Knowing exactly how marks are awarded, and where negative marking applies, shapes smarter decision-making during both practice and the real exam.'],
                  ['🎯', 'Choose the Right Subjects', 'Domain subject selection should align with the undergraduate programmes a student is targeting — this decision affects everything that follows, so it is worth settling early.'],
                  ['🔍', 'Analyse the Syllabus', 'Breaking the syllabus into topics and sub-topics before building a study plan ensures nothing important gets missed in the rush of preparation.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="hc-exam-card">
                    <div className="hc-exam-icon">{icon}</div>
                    <div><h3>{title}</h3><p>{desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* 10 tips */}
            <section id="ten-tips" className="hc-section">
              <h2 className="hc-h2">10 Proven Tips to Crack CUET Successfully</h2>
              <div className="hc-tips-numbered">
                {TEN_TIPS.map(t => (
                  <div key={t.n} className="hc-tip-card">
                    <div className="hc-tip-icon-badge">
                      <span className="hc-tip-num">{t.n}</span>
                      <span className="hc-tip-emoji">{t.icon}</span>
                    </div>
                    <div><h3>{t.title}</h3><p>{t.desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Subject-wise */}
            <section id="subject-wise" className="hc-section">
              <h2 className="hc-h2">Subject-wise Preparation Tips</h2>
              <div className="hc-subject-grid">
                <div className="hc-sub-card">
                  <h3>📖 Language Section</h3>
                  <p>Build reading speed through regular comprehension practice and strengthen vocabulary gradually, practising within the section's actual time limit rather than untimed. Consistent daily reading matters more here than intensive cramming sessions.</p>
                </div>
                <div className="hc-sub-card">
                  <h3>📚 Domain Subjects</h3>
                  <p>Anchor preparation in NCERT, reinforce with topic-wise practice questions, and revisit high-weightage chapters more than once across the preparation window. CUET's domain questions map closely to NCERT.</p>
                </div>
                <div className="hc-sub-card" style={{ gridColumn: '1 / -1' }}>
                  <h3>🧠 General Test</h3>
                  <ul>
                    <li><strong>Quantitative Aptitude:</strong> Practise regularly to build both speed and accuracy together</li>
                    <li><strong>Logical Reasoning:</strong> Focus on recognising recurring question patterns through repeated practice</li>
                    <li><strong>General Knowledge:</strong> Review key facts on a rolling basis rather than in one large push close to the exam</li>
                    <li><strong>Current Affairs:</strong> Follow updates consistently — this section changes most between exam cycles</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 60-day roadmap */}
            <section id="roadmap" className="hc-section">
              <h2 className="hc-h2">60-Day CUET Preparation Roadmap</h2>
              <p className="hc-p" style={{ marginBottom: '1rem' }}>This roadmap assumes daily, consistent effort. Students starting with less than 60 days should compress phases rather than skip them.</p>
              <div style={{ overflowX: 'auto', borderRadius: 12 }}>
                <table className="hc-roadmap-table">
                  <thead><tr><th>Phase</th><th>Days</th><th>Focus</th><th>Key Activities</th></tr></thead>
                  <tbody>
                    {[
                      ['Phase 1', 'Days 1–15', 'Syllabus & Concept Building', 'Review full syllabus by subject, complete first NCERT pass, identify weak areas and prioritise them.'],
                      ['Phase 2', 'Days 16–30', 'Topic-wise Practice', 'Solve focused question sets immediately after each topic, revisit NCERT where gaps appear, begin timed topic tests.'],
                      ['Phase 3', 'Days 31–45', 'PYQ & Mock Tests', 'Solve previous year papers, attempt at least two full-length mocks, analyse mistakes section by section.'],
                      ['Phase 4', 'Days 46–60', 'Full Mocks & Final Revision', 'Two to three full mocks in exam conditions, targeted revision of lowest-scoring areas, consolidate rather than introduce new material.'],
                    ].map(([phase, days, focus, activities]) => (
                      <tr key={phase}>
                        <td><span className="hc-phase-badge">{phase}</span></td>
                        <td style={{ fontWeight: 600, color: '#d4af37', whiteSpace: 'nowrap' }}>{days}</td>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{focus}</td>
                        <td>{activities}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Common mistakes */}
            <section id="mistakes" className="hc-section">
              <h2 className="hc-h2">Common Mistakes That Prevent Students from Cracking CUET</h2>
              <div className="hc-mistakes-grid">
                {[
                  ['Ignoring NCERT books', 'Favouring scattered, unverified material over NCERT, which is where CUET questions are primarily drawn from.'],
                  ['Studying without a plan', 'Leads to uneven syllabus coverage — some topics over-studied, others barely touched before exam day.'],
                  ['Skipping revision', 'Treating a single read-through as sufficient — topics covered early in preparation fade without deliberate revisiting.'],
                  ['Avoiding mock tests until too late', 'Losing weeks of format familiarity that regular testing would have built incrementally.'],
                  ['Poor time management', 'Both in daily study sessions and within timed practice — section timing must be practised, not assumed.'],
                  ['Learning new topics last minute', 'At the expense of revising what has already been studied — new material recalled under rush rarely sticks under pressure.'],
                  ['Not analysing mock test performance', 'Repeating the same avoidable mistakes across multiple tests because the root cause was never identified.'],
                ].map(([title, desc]) => (
                  <div key={title} className="hc-mistake">
                    <span className="hc-mistake-x">✕</span>
                    <p><strong>{title}: </strong>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Resources */}
            <section id="resources" className="hc-section">
              <h2 className="hc-h2">Best Resources for CUET Preparation</h2>
              <div className="hc-resources-grid">
                {[
                  ['📚', 'NCERT Books', 'The primary foundation for most domain subjects tested in CUET — concepts here map directly to what is tested.'],
                  ['📄', 'Previous Year Question Papers', "Critical for understanding NTA's actual question patterns and frequently tested topics."],
                  ['📝', 'Mock Tests', 'The closest available simulation of real exam-day conditions.'],
                  ['✏️', 'Practice Questions', 'Topic-wise practice reinforces concepts immediately after they are studied, rather than leaving gaps for later.'],
                  ['🌐', 'Online Coaching', "A structured platform pulls all of the above together into one coherent plan — this is where BESST's study materials and structured learning support fit directly into the tips outlined above."],
                  ['📊', 'Performance Analytics', 'Turning test results into specific next actions rather than just reviewing a score number.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="hc-res-card">
                    <div className="hc-res-icon">{icon}</div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* BESST helps */}
            <section id="besst-helps" className="hc-section">
              <h2 className="hc-h2">How BESST Helps Students Crack CUET</h2>
              <div className="hc-besst-grid">
                {BESST_FEATURES.map(f => (
                  <div key={f.title} className="hc-besst-card">
                    <div className="hc-besst-icon">{f.icon}</div>
                    <div><h3>{f.title}</h3><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
                <Link to="/online-cuet-coaching" className="btn-gold">Join BESST's CUET Online Coaching →</Link>
              </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="hc-section">
              <h2 className="hc-h2">Self-Study vs Online Coaching – Which Helps You Crack CUET?</h2>
              <div className="hc-cmp-wrap">
                <table className="hc-cmp-table">
                  <thead><tr><th>Factors</th><th>Self-Study</th><th>BESST Online Coaching</th></tr></thead>
                  <tbody>
                    {[
                      ['Structured Learning', 'Limited', '✔'],
                      ['Expert Guidance', '✘', '✔'],
                      ['Live Classes', '✘', '✔'],
                      ['Mock Tests', 'Limited', '✔'],
                      ['Performance Analysis', '✘', '✔'],
                      ['Doubt Resolution', '✘', '✔'],
                      ['Personal Mentorship', '✘', '✔'],
                    ].map(([f, self, besst]) => (
                      <tr key={f}>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{f}</td>
                        <td style={{ color: '#9ca3af' }}>{self}</td>
                        <td className="hc-better">{besst}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="hc-p" style={{ marginTop: '1rem' }}>Self-study can work well for highly disciplined students comfortable building their own schedule. For most first-time aspirants, guided coaching adds the most value in exactly the areas hardest to replicate alone — consistent mock testing, structured feedback, and progress tracking against a realistic timeline.</p>
            </section>

            {/* Exam-day tips */}
            <section id="exam-day" className="hc-section">
              <h2 className="hc-h2">Exam-Day Tips to Maximise Your CUET Score</h2>
              <div className="hc-exam-tips">
                {[
                  'Read instructions carefully before starting each section — small format details matter under CUET\'s structure',
                  'Attempt easier questions first to secure marks quickly and build momentum',
                  "Manage time effectively, keeping an eye on the clock section by section rather than only at the end",
                  "Avoid excessive guesswork where negative marking applies — a calculated skip is often better than a random guess",
                  'Stay calm and focused, especially if an early question feels harder than expected',
                  'Review answers if time permits, catching avoidable errors before submitting',
                ].map(tip => (
                  <div key={tip} className="hc-exam-tip"><span className="hc-exam-tip-check">✓</span><span>{tip}</span></div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="hc-section">
              <h2 className="hc-h2">Frequently Asked Questions (FAQs)</h2>
              <div className="hc-faq-list">
                {FAQS.map((f, i) => (
                  <div key={i} className={`hc-faq-item ${openFaq === i ? 'open' : ''}`}>
                    <button className="hc-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                      <span>{f.q}</span><span className="hc-faq-chevron">▼</span>
                    </button>
                    <div className="hc-faq-a" aria-hidden={openFaq !== i}>
                      <div className="hc-faq-a-inner">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Internal links */}
            <section className="hc-section">
              <h2 className="hc-h2">Explore More CUET Resources</h2>
              <div className="hc-links-grid">
                {[
                  ['/cuet-coaching', '📚 CUET Coaching (Pillar)'],
                  ['/online-cuet-coaching', '💻 Online CUET Coaching'],
                  ['/cuet-preparation-strategy', '🎯 CUET Preparation Strategy'],
                  ['/cuet-study-plan', '🗓️ CUET Study Plan'],
                  ['/cuet-mock-test', '📝 CUET Mock Test'],
                  ['/cuet-mock-test/free', '🆓 Free CUET Mock Test'],
                  ['/cuet-previous-year-question-papers', '📄 CUET PYQ Papers'],
                  ['/cuet-syllabus', '📋 CUET Syllabus'],
                  ['/cuet-exam-pattern', '📐 CUET Exam Pattern'],
                  ['/cuet-registration', '📝 CUET Registration'],
                  ['/cuet-subject-list', '📑 CUET Subject List'],
                  ['/cuet-cutoff', '📊 CUET Cutoff'],
                  ['/cuet-counselling', '🎓 CUET Counselling'],
                ].map(([to, label]) => (
                  <Link key={to} to={to} className="hc-ilink">
                    <span>{label}</span>
                    <span style={{ marginLeft: 'auto', opacity: .5 }}>→</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="hc-cta">
              <h2>Ready to Crack CUET with Expert Guidance?</h2>
              <p>
                Success in CUET isn't about studying harder — it's about studying smarter. BESST's CUET Online Coaching provides expert-led live classes, comprehensive study materials, mock tests, personalised mentorship, and performance tracking to help you prepare with confidence.
              </p>
              <Link to="/subscriptions" className="btn-gold">Start Preparing with BESST →</Link>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default HowToCrackCuetPage;
